import type * as React from "react";
import { type ReactNode } from "react";

import classNames from "classnames";
import * as PriceUtils from "~/utils/PriceUtils";
import { zero } from "~/utils/PriceUtils";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import Spacer from "~/common/components/Spacer";
import SimplePriceText, { PriceTextVariants } from "~/domains/pricing/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { AliasesParameter } from "~/domains/product/components/ProductParams";
import { getTotalPrice, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { obtainSpecialPriceLabel } from "~/domains/pricing/components/Price/v1/Price";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { Stack } from "~/common/mui";
import { FCCTag } from "~/features/freeCapacityPush/components/FCCTag/FCCTag";
import classes from "~/features/cart/activeQuote/components/productTable/EditablePackageEntry.module.less";
import { Alert } from "~/common/mui/Alert/Alert";
import { ViewablePackagingCellOld } from "~/features/cart/activeQuote/components/productTable/PackagingCellOld";
import type { QuotePackageEntryViewModel } from "~/features/cart/activeQuote/models/QuoteTableViewModel";

interface Props {
  entryViewModel: QuotePackageEntryViewModel;
  index: number;
}

const ViewablePackageEntry = ({ entryViewModel }: Props) => {
  const { t } = useTranslation();
  const packageEntry = entryViewModel;
  const product = entryViewModel.product;

  const isCommentVisible = packageEntry.showComments;

  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;

  const { visiblePrice } = packageEntry;
  const priceUnit = visiblePrice.unit;

  const isSpecial = isSpecialPrice(product, packageEntry.packCode ?? "");

  const rdd = packageEntry.deliveryDate ? formatDate(packageEntry.deliveryDate, DateFormatType.DATE) : "–";

  const total = getTotalPrice(visiblePrice, packageEntry.quantity);

  const isFcc = product.base.fifteenDigit[packageEntry.packCode]?.isFcc;

  return (
    <div data-test-id={`package-entry`}>
      <CellRow>
        <Cell data-test-id={"package-entry-quantity"} first={true} className={classes.qtyCell}>
          {packageEntry.quantity}
          {isFcc && <FCCTag anchor={"left"} className={classes.fccTag} />}
        </Cell>

        <Cell>
          <div>
            <ViewablePackagingCellOld
              lineItem={
                {
                  packSize: packageEntry.packSize,
                  packCode: packageEntry.packCode,
                  quantity: packageEntry.quantity,
                  primaryWarehouseName: packageEntry.primaryWarehouseName,
                  source: packageEntry.source,
                } as Parameters<typeof ViewablePackagingCellOld>[0]["lineItem"]
              }
              product={product}
            />

            <AliasesParameter
              className={"mt-3"}
              product={product.base}
              mode={"15-digits"}
              packCode={packageEntry.packCode}
            />
          </div>
        </Cell>

        <Cell>
          {quantityVisible ? (
            <div>
              <p className={"my-0"}>{rdd}</p>
            </div>
          ) : (
            <div>{"-"}</div>
          )}
        </Cell>

        <Cell className={"d-flex flex-direction-column"}>
          <Stack gap={1}>
            <div>
              <div data-test-id={"package-entry-offer-price"}>{PriceUtils.text(visiblePrice || zero())}</div>
              <div className={"mt-1 f-label-color"}>
                {
                  <SimplePriceText
                    priceUnit={priceUnit}
                    variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                  />
                }
              </div>
              {isSpecial && <div className={"scha__customer-special-price"}>{obtainSpecialPriceLabel(t)}</div>}
            </div>
            <PriceReferenceParam
              product={product.base}
              priceReference={packageEntry.priceReference}
              data-test-id={"package-entry-price-reference"}
              disabled={true}
              editable={false}
            />
          </Stack>
        </Cell>

        <Cell className={"d-flex flex-direction-column"} last={true}>
          <strong data-test-id={"package-entry-total-price"}>{PriceUtils.text(total)}</strong>
          {!quantityVisible && (
            <div className={"mt-1 f-label-color"}>
              <SimplePriceText
                priceUnit={priceUnit}
                variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
              />
            </div>
          )}
        </Cell>
      </CellRow>

      {isCommentVisible && (
        <Row className={"mt-3"}>
          <p className={"my-0 f-label-color"}>{t("activeQuotePage.comment.title")}</p>
          <p className={"mt-1 mb-0"} data-test-id={"cart-package-entry-comment"}>
            {packageEntry.comment}
          </p>
        </Row>
      )}
      {/* spacer without border */}
      {(!quantityVisible || isOptime) && <div className={"mt-2 mb-0"}></div>}
      {!quantityVisible && (
        <Alert severity="info" className="mt-2 w-100">
          {t("subscriptionProduct.DST.billingInfo")}
        </Alert>
      )}
      {isProductPMA(product) && <InfoProductMessagePMA className={"mt-2"} />}
      {isOptime && <OptimeSolutionsMessage className="mt-2 w-100" />}
      <Spacer className={"mt-3 mb-0"} />
    </div>
  );
};

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__active-quote__product-table__right-side__col-container"}>{children}</div>
);

const Cell: React.FunctionComponent<{
  alignEnd?: boolean;
  first?: boolean;
  last?: boolean;
  className?: string;
  "data-test-id"?: string;
  children?: ReactNode;
}> = ({ children, alignEnd, first, last, className, ...rest }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
};

const Row: React.FunctionComponent<{ className?: string; children?: ReactNode }> = ({ children, className }) => (
  <div className={classNames(className, "px-3")}>{children}</div>
);

export default ViewablePackageEntry;
